import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";

import { useTesterIdentity } from "./TesterIdentityProvider";

const STALE_LOCK_MS = 20 * 60 * 1000;
const FORCE_UNLOCK_AFTER_MS = 18 * 60 * 1000;

const toMillis = (value) => {
  if (!value) return null;
  if (typeof value.toMillis === "function") return value.toMillis();
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

const isFresh = (lock, nowMs) => {
  const startedMs = toMillis(lock.startedAt);
  if (startedMs === null) return true;
  return nowMs - startedMs <= STALE_LOCK_MS;
};

// Subscribes to the tester-fix-locks collection and exposes the live
// state needed by the bug chat UI: own lock, other-tester locks, plus
// derived flags for showing the warning banner / force-unlock button.
const useFixLocks = () => {
  const { internalDb, tester } = useTesterIdentity();
  const [locks, setLocks] = useState([]);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!internalDb) {
      setLocks([]);
      setSubscriptionError(null);
      return undefined;
    }
    const unsubscribe = onSnapshot(
      collection(internalDb, "tester-fix-locks"),
      (snap) => {
        const docs = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        setLocks(docs);
        setSubscriptionError(null);
      },
      (err) => {
        console.warn("[tester-chat] fix locks subscription error:", err.message);
        setLocks([]);
        setSubscriptionError(err.message || "Lock subscription failed");
      }
    );
    return unsubscribe;
  }, [internalDb]);

  useEffect(() => {
    const interval = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(interval);
  }, []);

  return useMemo(() => {
    const fresh = locks.filter((lock) => isFresh(lock, nowMs));
    const ownLock = tester?.id
      ? fresh.find((lock) => lock.testerId === tester.id) || null
      : null;
    const otherLocks = tester?.id
      ? fresh.filter((lock) => lock.testerId !== tester.id)
      : fresh;
    const ownLockAgeMs = ownLock ? nowMs - (toMillis(ownLock.startedAt) || nowMs) : 0;
    return {
      ownLock,
      otherLocks,
      ownLockAgeMs,
      canForceUnlock: ownLock && ownLockAgeMs >= FORCE_UNLOCK_AFTER_MS,
      subscriptionError
    };
  }, [locks, tester?.id, nowMs, subscriptionError]);
};

export default useFixLocks;
